import { DialogM } from "@common/components/alert/dialog/Dialog"
import { Menu3Point } from "@common/components/menu/Menu3Point"
import ObjectiveSubjectsBadges from "@common/components/objectives/ObjectiveSubjectsBadges"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useIsFocusedM } from "@common/hooks/useIsFocusedM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import { apiRequest } from "@data/redux/actions/api"
import { setRecentlyOpened } from "@data/redux/actions/camera"
import { attendanceFlagsService } from "@data/services/attendanceFlagsService"
import { liveService } from "@data/services/liveService"
import { LIVE_DAY, liveDayFromToday } from "@data/utility/liveDay"
import { formatDate, formatMinutes, readableDayTime } from "@data/utility/Utils"
import { useNavigation } from "@react-navigation/native"
import * as WebBrowser from "expo-web-browser"
import moment from "moment"
import React, { useEffect } from "react"
import { View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import AvatarM from "../../common/components/avatar/AvatarM"
import FilledButtonM from "../../common/components/button/FilledButtonM"
import TextM from "../../common/components/text/TextM"
import { findClosestNextOccurrence } from "../../student/account/onboarding/LiveRoomCard"
import { showLiveNotTodayDialog } from "./showLiveNotTodayDialog"
import { useReloadBeforeRefusing } from "./useReloadBeforeRefusing"

// Reusable status badge component
const StatusBadge = ({ icon, label, backgroundColor, color }) => (
    <View
        style={{
            flexDirection: "row",
            alignItems: "center",
            backgroundColor,
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 10,
            marginLeft: 10,
        }}
    >
        <FontAwesome5 name={icon} solid color={color} size={10} />
        <TextM
            fontSize={10}
            fontWeight="SemiBold"
            color={color}
            style={{ marginLeft: 4 }}
        >
            {label}
        </TextM>
    </View>
)

const StudentLiveCardComponent = (props) => {
    const {
        liveSession,
        liveSessionsForOneRoom,
        dateTime = null,
        noButtons = false,
        customTitle = null,
        noAvatar = false,
        style = {},
        oneOccurrence = true,
        navigation = null,
        additionalOnPress = () => {},
        subscriptionUser = null,
        noButton = false,
        handleShowModal = () => {},
        handleShowSendTutorMessageModal = () => {},
        setSendTutorMessageTutor,
        occurrence = null,
        disableCameraReopenEffect = false,
    } = props

    const nav = navigation ?? useNavigation()
    const isFocused = useIsFocusedM()

    const paymentBlocked = props.paymentBlocked
    const paymentUrl = props.paymentUrl
    const onPaymentBlockedPress = props.onPaymentBlockedPress

    const closestNextOccurrence = findClosestNextOccurrence([liveSession])

    const user = subscriptionUser ?? props.user

    const closestNextLiveSession = liveSessionsForOneRoom?.find(
        (x) => x?.id === closestNextOccurrence?.live_session_id
    )

    const liveMoment = closestNextOccurrence?.date
        ? moment(closestNextOccurrence?.date)
        : null

    const isIndividual = liveSession?.live_room?.type === "individual"

    // Get the tutor for this occurrence - use occurrence.tutor as source of truth if available
    const occurrenceTutor = occurrence?.tutor || liveSession?.tutor

    const occurrenceDate = dateTime || closestNextOccurrence?.date
    const isToday = liveDayFromToday(occurrenceDate) === LIVE_DAY.TODAY

    const answerGo = ({ canReload }) => {
        const liveDay = liveDayFromToday(occurrenceDate)
        if (liveDay === LIVE_DAY.TODAY) {
            if (!liveSession?.students?.find((x) => x?.id === user?.id)) {
                DialogM.show({
                    champion: "zoe",
                    variant: "main",
                    text1: "Tu n'es pas inscrit à cette session live",
                    text2: "Tu ne peux pas y accéder",
                    exitButton: true,
                    onPressBack: () => {},
                })
                return
            }
            nav.push("pre-call", { liveSessionId: liveSession?.id })
        } else if (canReload) {
            reloadThenAnswer()
        } else if (liveDay === LIVE_DAY.FUTURE) {
            showLiveNotTodayDialog(occurrenceDate)
        }
    }
    const { reloadThenAnswer, isReloadingForGo } = useReloadBeforeRefusing({
        key: `live-card-${liveSession?.id}-${occurrenceDate}`,
        data: props.liveOccurrences,
        isReloading: props.getAllMyNextLiveSessionsRequest,
        reload: () => props.apiRequest(liveService.getAllMyNextLiveSessions),
        answer: () => answerGo({ canReload: false }),
        canAnswer: isFocused,
    })

    // Find the relevant occurrence for the given dateTime or use the closest one
    const relevantOccurrence = dateTime
        ? liveSession?.next_occurrences?.find((occ) => occ.date === dateTime)
        : closestNextOccurrence

    const isReplacement = relevantOccurrence?.substitution_id
    const isRescheduled = relevantOccurrence?.rescheduling_id
    const isFinalized = relevantOccurrence?.finalized_at

    const liveRoom = {
        ...liveSession?.live_room,
        live_sessions: liveSessionsForOneRoom,
    }

    // Check if there's a "late" flag for this specific occurrence
    const lateFlags = liveSession?.attendance_flags?.filter(
        (flag) =>
            flag.type === "late" &&
            flag.user_id === user?.id &&
            flag.date === occurrenceDate
    )
    // Get the last (most recent) late flag
    const lateFlag = lateFlags?.[lateFlags.length - 1]

    // Check if there's an "absent" flag for this specific occurrence
    const absenceFlags = liveSession?.attendance_flags?.filter(
        (flag) =>
            flag.type === "absent" &&
            flag.user_id === user?.id &&
            flag.date === occurrenceDate
    )

    const hasAbsenceFlag = absenceFlags && absenceFlags.length > 0
    const absenceFlagId = absenceFlags?.[0]?.id
    const absenceHasRecovery =
        absenceFlags?.[0]?.recovery_temporary_assignment_id

    // Check if this occurrence is a recovery for an absence
    const recoveryForAbsenceFlag =
        occurrence?.id && props.liveOccurrences
            ? props.liveOccurrences
                  .flatMap((occ) => occ?.live_session?.attendance_flags || [])
                  .find(
                      (flag) =>
                          flag.type === "absent" &&
                          flag.user_id === user?.id &&
                          flag.recovery_live_occurrence_id === occurrence.id
                  )
            : null

    // A recovery session is one where an attendance flag points to this occurrence as recovery
    const isRecovery = !!recoveryForAbsenceFlag

    // Check if student is temporarily added to this occurrence
    // The is_temporary attribute is always set by the backend
    const isTemporarilyAdded = occurrence?.is_temporary

    // Recovery display conditions
    // Don't show recovery info if there's an absence flag on this occurrence
    // (if student is absent on the rattrapage, they won't attend, so the recovery badge is not relevant)
    const shouldShowRecoveryWithAbsenceDate =
        isRecovery && recoveryForAbsenceFlag && !hasAbsenceFlag
    const isHolidayCourse = liveSession?.live_room?.type === "holiday_course"
    const shouldShowRecoveryBadge =
        !hasAbsenceFlag &&
        !isHolidayCourse &&
        ((isRecovery && recoveryForAbsenceFlag) ||
            (isTemporarilyAdded && !isRecovery))
    const shouldShowCancelRecoveryInParentView =
        shouldShowRecoveryWithAbsenceDate && subscriptionUser

    // Reopen modal after camera if this card's modal should be reopened and we're on the focused screen
    useEffect(() => {
        if (
            !disableCameraReopenEffect &&
            props.recentlyOpenedCam &&
            handleShowSendTutorMessageModal &&
            isFocused
        ) {
            handleShowSendTutorMessageModal()
            props.setRecentlyOpenedCam(false)
        }
    }, [props.recentlyOpenedCam, isFocused, disableCameraReopenEffect])

    // Get all flag IDs that belong to this card for error handling
    const thisFlagIds = [
        absenceFlagId,
        lateFlag?.id,
        recoveryForAbsenceFlag?.id,
    ].filter(Boolean)

    // Handle cancel attendance flag API error
    // Only show error if it was for a flag on this card
    const isThisCardFlag = thisFlagIds.includes(props.cancelAttendanceFlagId)

    useApiStatus(
        attendanceFlagsService.cancelAttendanceFlag,
        null, // No success message - Redux state update handles UI refresh
        isThisCardFlag, // Only show error dialog if it's for this card's flags
        () => {}
    )

    // Helper to build the recovery cancellation warning message
    const getRecoveryMessage = (isFromRecovery) => {
        if (isFromRecovery) {
            // We're on the recovery card, show the recovery date (current occurrence)
            return `\n\nLe rattrapage prévu le ${formatDate(
                occurrenceDate
            )} à ${moment(occurrenceDate).format(
                "HH:mm"
            )} sera également annulé.`
        }

        // We're on the absence card, find the recovery occurrence from the flag
        const absenceFlag = absenceFlags?.[0]
        if (
            !absenceFlag?.recovery_live_occurrence_id ||
            !props.liveOccurrences
        ) {
            return absenceHasRecovery
                ? "\n\nLe rattrapage planifié sera également annulé."
                : ""
        }

        const recoveryOccurrence = props.liveOccurrences.find(
            (occ) => occ.id === absenceFlag.recovery_live_occurrence_id
        )
        return recoveryOccurrence
            ? `\n\nLe rattrapage prévu le ${formatDate(
                  recoveryOccurrence.date
              )} à ${moment(recoveryOccurrence.date).format(
                  "HH:mm"
              )} sera également annulé.`
            : "\n\nLe rattrapage planifié sera également annulé."
    }

    const handleCancelFlag = (flagId, flagType, isFromRecovery = false) => {
        const getFlagToCancel = () => {
            if (isFromRecovery) return recoveryForAbsenceFlag
            if (flagType === "absent") return absenceFlags?.[0]
            return lateFlag
        }
        const flagToCancel = getFlagToCancel()

        const dateToShow = isFromRecovery ? flagToCancel?.date : occurrenceDate
        const isAbsence = flagType === "absent"
        const recoveryMessage = isAbsence
            ? getRecoveryMessage(isFromRecovery)
            : ""

        DialogM.show({
            text1: isAbsence ? "Annuler l'absence ?" : "Annuler le retard ?",
            text2: `Êtes-vous sûr de vouloir annuler ${
                isAbsence ? "l'absence" : "le retard"
            } du ${formatDate(dateToShow)} à ${moment(dateToShow).format(
                "HH:mm"
            )} ?${recoveryMessage}`,
            buttonText1: "Oui, annuler",
            buttonColor1: theme.colors.error,
            onPress: () => {
                props.apiRequest(attendanceFlagsService.cancelAttendanceFlag, {
                    id: flagId,
                })
            },
            buttonText2: "Non",
            onPressBack: () => {},
        })
    }

    // Helper to compute the time display text
    const getTimeDisplayText = () => {
        if (customTitle) return customTitle
        if (oneOccurrence) {
            const time = moment(dateTime ?? liveMoment).format("HH:mm")
            return `${time} - ${formatMinutes(liveSession?.duration)}`
        }
        return readableDayTime(liveSession?.day_time, liveSession?.duration)
    }

    // Build menu items for 3-dot menu
    // Important: When absent, original UI only showed "Annuler l'absence" - not the other actions
    // When finalized, no actions are available
    const buildMenuItems = () => {
        const items = []

        // When finalized: no actions available
        if (isFinalized) {
            return items
        }

        // When absent: only show cancel absence option (and message for parent)
        if (hasAbsenceFlag) {
            items.push({
                title: "Annuler l'absence",
                icon: "times-circle",
                onPress: () => handleCancelFlag(absenceFlagId, "absent"),
            })
            // Parent can still message tutor even for absent sessions
            if (subscriptionUser && occurrenceTutor) {
                items.push({
                    title: "Message au tuteur",
                    icon: "envelope",
                    onPress: () => {
                        handleShowModal()
                        setSendTutorMessageTutor(occurrenceTutor)
                        handleShowSendTutorMessageModal()
                    },
                })
            }
            return items
        }

        // Normal/active session: show management options
        const roomType = closestNextLiveSession?.live_room?.type
        const isGroupSession = [
            "biweekly",
            "study_group",
            "holiday_course",
            "holiday_workbook",
        ].includes(roomType)
        const isTrial = roomType === "trial"

        // Get session type label for individual-style sessions
        const getSessionTypeLabel = () => {
            switch (roomType) {
                case "support":
                    return "Live de support technique"
                case "custom":
                    return "Live personnalisé"
                default:
                    return "Séance individuelle"
            }
        }

        items.push({
            title: "Gérer le rdv",
            icon: "calendar-alt",
            onPress: () => {
                if (isGroupSession) {
                    // Group sessions: navigate to absence form
                    const params = {
                        id: user.id,
                        liveSessionId: closestNextLiveSession?.id,
                        liveRoomId: closestNextLiveSession?.live_room_id,
                        withStudent: !!subscriptionUser,
                    }
                    if (occurrence) {
                        params.occurrenceId = occurrence.id
                        params.occurrenceDate = occurrence.date
                    }
                    navigation.push("absence-form", params)
                    handleShowModal()
                } else if (isTrial) {
                    // Trial sessions: contact central team (tutor can't help)
                    DialogM.show({
                        text1: "Séance d'essai",
                        text2: "Pour toute question concernant votre séance d'essai (report, annulation), contactez notre équipe.",
                        buttonText1: "WhatsApp",
                        onPress: () => {
                            const url = "https://wa.me/message/FCOBQ6PCPMBJC1"
                            if (IS_WEB) {
                                window.open(url)
                            } else {
                                WebBrowser.openBrowserAsync(url)
                            }
                        },
                        buttonText2: "Email",
                        onPressBack: () => {
                            const url = "mailto:support@masteur.com"
                            if (IS_WEB) {
                                window.open(url)
                            } else {
                                WebBrowser.openBrowserAsync(url)
                            }
                        },
                    })
                    handleShowModal()
                } else {
                    // Individual/support/custom sessions: contact tutor directly
                    DialogM.show({
                        text1: getSessionTypeLabel(),
                        text2: "Pour toute question de planification (absence, retard, déplacement de séance), contactez directement votre tuteur. Il pourra vous répondre et adapter le rdv si besoin.",
                        buttonText1: subscriptionUser
                            ? "Contacter le tuteur"
                            : "Contacter mon tuteur",
                        onPress: () => {
                            if (subscriptionUser) {
                                handleShowModal()
                                setSendTutorMessageTutor(occurrenceTutor)
                                handleShowSendTutorMessageModal()
                            } else {
                                navigation.navigate("messenger", {
                                    tutorId: occurrenceTutor?.id,
                                })
                            }
                        },
                        buttonText2: "Retour",
                        onPressBack: () => {},
                    })
                    handleShowModal()
                }
            },
        })

        // Message au tuteur (parent view only)
        if (subscriptionUser && occurrenceTutor) {
            items.push({
                title: "Message au tuteur",
                icon: "envelope",
                onPress: () => {
                    handleShowModal()
                    setSendTutorMessageTutor(occurrenceTutor)
                    handleShowSendTutorMessageModal()
                },
            })
        }

        // Cancel late option (only if late, not absent)
        if (lateFlag) {
            items.push({
                title: "Annuler le retard",
                icon: "times-circle",
                onPress: () => handleCancelFlag(lateFlag.id, "late"),
            })
        }

        // Cancel recovery option (parent view only for recovery sessions)
        if (shouldShowCancelRecoveryInParentView) {
            items.push({
                title: "Annuler le rattrapage",
                icon: "times-circle",
                onPress: () =>
                    handleCancelFlag(recoveryForAbsenceFlag.id, "absent", true),
            })
        }

        return items
    }

    return (
        <View style={{ position: "relative", width: "100%", ...style }}>
            <View
                style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    marginVertical: 4,
                    backgroundColor:
                        hasAbsenceFlag || paymentBlocked
                            ? theme.colors.lightMore
                            : theme.colors.white,
                    borderTopWidth: 1,
                    borderRightWidth: 1,
                    borderBottomWidth: 1,
                    borderLeftWidth:
                        isToday && !hasAbsenceFlag && !paymentBlocked ? 4 : 1,
                    borderTopColor: theme.colors.lightMore,
                    borderRightColor: theme.colors.lightMore,
                    borderBottomColor: theme.colors.lightMore,
                    borderLeftColor:
                        isToday && !hasAbsenceFlag && !paymentBlocked
                            ? theme.colors.success
                            : theme.colors.lightMore,
                    width: "100%",
                    opacity: hasAbsenceFlag || paymentBlocked ? 0.7 : 1,
                }}
            >
                <View
                    style={{
                        paddingHorizontal: 12,
                        paddingVertical: 10,
                    }}
                >
                    {/* Top row: Time + badges + menu */}
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                flex: 1,
                            }}
                        >
                            {/* Time */}
                            <TextM
                                fontWeight={"Bold"}
                                fontSize={15}
                                color={theme.colors.primary}
                            >
                                {getTimeDisplayText()}
                            </TextM>

                            {/* Status badges */}
                            {paymentBlocked && !hasAbsenceFlag && (
                                <StatusBadge
                                    icon="lock"
                                    label="Paiement en attente"
                                    backgroundColor={theme.colors.warningLight}
                                    color={theme.colors.warning}
                                />
                            )}

                            {hasAbsenceFlag && (
                                <StatusBadge
                                    icon="times-circle"
                                    label="Absent"
                                    backgroundColor={theme.colors.errorLight}
                                    color={theme.colors.error}
                                />
                            )}

                            {lateFlag && !hasAbsenceFlag && (
                                <StatusBadge
                                    icon="clock"
                                    label="Retard"
                                    backgroundColor={theme.colors.warningLight}
                                    color={theme.colors.warning}
                                />
                            )}

                            {shouldShowRecoveryBadge && (
                                <StatusBadge
                                    icon="redo"
                                    label="Rattrapage"
                                    backgroundColor={
                                        theme.colors.primaryLighter
                                    }
                                    color={theme.colors.primary}
                                />
                            )}

                            {(isRescheduled || isReplacement) && (
                                <StatusBadge
                                    icon={
                                        isReplacement
                                            ? "user-friends"
                                            : "calendar-alt"
                                    }
                                    label={
                                        isReplacement
                                            ? "Remplaçant"
                                            : "Replanifié"
                                    }
                                    backgroundColor={theme.colors.lightMore}
                                    color={theme.colors.grey}
                                />
                            )}

                            {isFinalized && (
                                <StatusBadge
                                    icon="check-circle"
                                    label="Terminé"
                                    backgroundColor={theme.colors.lightMore}
                                    color={theme.colors.grey}
                                />
                            )}
                        </View>

                        {/* 3-dot menu - hidden when finalized */}
                        {!noButtons && !isFinalized && (
                            <Menu3Point
                                menuItems={buildMenuItems()}
                                color={theme.colors.grey}
                                iconSize={18}
                            />
                        )}
                    </View>

                    {/* Recovery info text */}
                    {shouldShowRecoveryWithAbsenceDate && (
                        <TextM
                            fontWeight={"Light"}
                            fontSize={10}
                            color={theme.colors.grey}
                            style={{ marginBottom: 6 }}
                        >
                            Rattrapage de l'absence du{" "}
                            {formatDate(recoveryForAbsenceFlag.date)} à{" "}
                            {moment(recoveryForAbsenceFlag.date).format(
                                "HH:mm"
                            )}
                        </TextM>
                    )}

                    {/* Tutor row */}
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                flex: 1,
                            }}
                        >
                            {!noAvatar && (
                                <AvatarM
                                    size={28}
                                    user={occurrenceTutor}
                                    style={{ borderRadius: 14 }}
                                />
                            )}
                            <TextM
                                style={{ marginLeft: 8 }}
                                fontSize={13}
                                fontWeight="Medium"
                                color={theme.colors.dark}
                            >
                                {occurrenceTutor?.display_name}
                            </TextM>
                            <ObjectiveSubjectsBadges
                                student={user}
                                liveSessionId={liveSession?.id}
                                schoolSubjects={props.schoolSubjects}
                                style={{ marginLeft: 10 }}
                            />
                        </View>

                        {/* GO button for students */}
                        {!noButton && !hasAbsenceFlag && !subscriptionUser && (
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <FilledButtonM
                                    icon={paymentBlocked ? "lock" : undefined}
                                    style={{
                                        width: null,
                                        marginBottom: 0,
                                    }}
                                    mode="contained"
                                    contentStyle={{ height: 30 }}
                                    labelStyle={{ fontSize: 11 }}
                                    label="GO !"
                                    loading={isReloadingForGo}
                                    onPress={() => {
                                        if (paymentBlocked) {
                                            onPaymentBlockedPress?.()
                                            return
                                        }
                                        additionalOnPress()
                                        if (isFinalized) {
                                            DialogM.show({
                                                champion: "zoe",
                                                variant: "main",
                                                text1: "Cette session est clôturée",
                                                text2: "Tu ne peux plus y accéder",
                                                exitButton: true,
                                                onPressBack: () => {},
                                            })
                                            return
                                        }
                                        answerGo({ canReload: true })
                                    }}
                                    color={
                                        paymentBlocked
                                            ? theme.colors.warning
                                            : isToday && !isFinalized
                                              ? theme.colors.success
                                              : theme.colors.lightMore
                                    }
                                />
                            </View>
                        )}
                    </View>

                    {/* Mes RDV info (when no occurrence) */}
                    {!occurrence && (
                        <TextM
                            color={theme.colors.grey}
                            fontWeight={"Light"}
                            fontSize={10}
                            wrap
                            style={{ marginTop: 6 }}
                        >
                            Mes RDV :{" "}
                            {liveRoom?.live_sessions
                                ?.map((ls) =>
                                    readableDayTime(
                                        ls?.day_time,
                                        ls?.duration,
                                        ls?.live_room?.type
                                    )
                                )
                                ?.join(", ")}
                            {isIndividual && " (Individuel)"}
                        </TextM>
                    )}
                </View>
            </View>
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        schoolSubjects: state.data.staticData?.school_subjects,
        recentlyOpenedCam: state.camera?.recentlyOpened,
        cancelAttendanceFlagId: state.api.cancelAttendanceFlag?.routeParams?.id,
        liveOccurrences: state.data.liveSessions?.live_occurrences,
        getAllMyNextLiveSessionsRequest:
            state.api.getAllMyNextLiveSessions?.request,
        paymentBlocked: state.data.currentUser?.object?.is_payment_blocked,
        paymentUrl: state.data.currentUser?.object?.payment_url,
    }
}

const mapDispatchToProps = {
    apiRequest,
    setRecentlyOpenedCam: setRecentlyOpened,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StudentLiveCardComponent)
